'use client';

import { useCallback, useRef, useState } from 'react';
import { Box, Chip, Divider, MenuItem, MenuList, Paper, Portal, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { useLanguage } from '@/contexts/LanguageContext';
import { useClickOutside, useMenuPosition, useT } from '@/hooks';

import { SUPPORTED_LANGUAGES } from '@/constants/language.constants';

interface LanguageSelectorProps {
  /** Compact mode – just shows flag + code (for header). Full mode shows full label too. */
  compact?: boolean;
}

export const LanguageSelector = ({ compact = true }: LanguageSelectorProps) => {
  const { language, setLanguage } = useLanguage();
  const queryClient = useQueryClient();
  const { t } = useT();
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => setIsOpen(false), []);
  const menuRef = useClickOutside<HTMLDivElement>(handleClose, isOpen);
  const menuPosition = useMenuPosition(anchorRef, isOpen);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen((prev) => !prev);
  };

  const handleSelect = (code: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setLanguage(code);
    setIsOpen(false);
    queryClient.invalidateQueries();
  };

  return (
    <>
      <Box ref={anchorRef} sx={{ position: 'relative' }}>
        <Chip
          id="language-selector-trigger"
          aria-label="Select language"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          // icon={
          //   <Box component="span" sx={{ fontSize: '1.1rem', ml: 0.5, display: 'flex' }}>
          //     {language.flag}
          //   </Box>
          // }
          label={
            compact ? (
              <Typography
                component="span"
                sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1 }}
              >
                {language.code.toUpperCase()}
              </Typography>
            ) : (
              <Typography component="span" sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
                {language.label}
              </Typography>
            )
          }
          onClick={handleToggle}
          variant="outlined"
          size="small"
          sx={{
            cursor: 'pointer',
            height: 32,
            borderRadius: '8px',
            borderColor: 'divider',
            bgcolor: isOpen ? 'action.selected' : 'background.paper',
            color: 'text.primary',
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: 'action.hover',
              borderColor: 'primary.main',
            },
            '& .MuiChip-icon': {
              color: 'text.secondary',
              ml: 0.5,
            },
            '& .MuiChip-label': { pr: 1 },
          }}
        />
      </Box>

      {isOpen && menuPosition && (
        <Portal>
          {/* Backdrop */}
          <Box
            onClick={handleClose}
            sx={{ position: 'fixed', inset: 0, zIndex: 11999, bgcolor: 'transparent' }}
          />

          {/* Menu */}
          <Paper
            ref={menuRef}
            elevation={8}
            sx={{
              position: 'fixed',
              top: `${menuPosition.top}px`,
              right: `${menuPosition.right}px`,
              minWidth: 220,
              maxHeight: 360,
              overflowY: 'auto',
              zIndex: 12000,
              borderRadius: '14px',
              bgcolor: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'rgba(30, 31, 35, 0.92)'
                  : 'rgba(255, 255, 255, 0.92)',
              backdropFilter: 'blur(16px)',
              border: '1px solid',
              borderColor: (theme) =>
                theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
              boxShadow: (theme) =>
                theme.palette.mode === 'dark'
                  ? '0 12px 48px -12px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.05)'
                  : '0 12px 48px -12px rgba(0,0,0,0.18)',
              animation: 'langSlideIn 0.22s cubic-bezier(0.4,0,0.2,1)',
              '@keyframes langSlideIn': {
                from: { opacity: 0, transform: 'translateY(-10px) scale(0.97)' },
                to: { opacity: 1, transform: 'translateY(0) scale(1)' },
              },
            }}
          >
            <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                letterSpacing={0.5}
              >
                {t('menu.selectLanguage')}
              </Typography>
            </Box>
            <Divider sx={{ my: 0.5 }} />
            <MenuList sx={{ py: 0.5 }} role="listbox" aria-label="Language options">
              {SUPPORTED_LANGUAGES.map((lang) => {
                const isSelected = lang.code === language.code;
                return (
                  <MenuItem
                    key={lang.code}
                    id={`language-option-${lang.code}`}
                    role="option"
                    aria-selected={isSelected}
                    selected={isSelected}
                    onClick={(e) => handleSelect(lang.code, e)}
                    sx={{
                      py: 0.85,
                      px: 1.75,
                      mx: 0.5,
                      borderRadius: '8px',
                      gap: 1.5,
                      transition: 'background-color 0.15s ease',
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        '&:hover': { bgcolor: 'primary.dark' },
                      },
                    }}
                  >
                    {/* <Typography component="span" sx={{ fontSize: '1.2rem', lineHeight: 1 }}>
                      {lang.flag}
                    </Typography> */}
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        fontWeight={isSelected ? 600 : 400}
                        color={isSelected ? 'inherit' : 'text.primary'}
                      >
                        {lang.label}
                      </Typography>
                    </Box>
                    {isSelected && (
                      <Typography variant="caption" sx={{ opacity: 0.75, fontWeight: 600 }}>
                        ✓
                      </Typography>
                    )}
                  </MenuItem>
                );
              })}
            </MenuList>
          </Paper>
        </Portal>
      )}
    </>
  );
};

export default LanguageSelector;
